import { ParseError } from 'papaparse';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ParseInvoiceErrorTypes = [
	'Unknown',
	'InsufficientData',
	'Unauthorized',
	'ServerTimeout',
	'MaxAllotted',
] as const;
type ParseInvoiceErrorType = (typeof ParseInvoiceErrorTypes)[number];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ParseInvoiceErrorCodes = [
	'Aborted',
	'MissingField',
	/** Retained for historical `analysis.errors`; extra columns are no longer rejected. */
	'ExtraField',
	'TooFewTimePeriods',
	'InvalidValue',
	'InvalidOperation',
	'TooManyRows',
] as const;
type ParseInvoiceErrorCode = (typeof ParseInvoiceErrorCodes)[number];

/**
 * In order to use the existing Papa Parser errors and codes on top
 * of adding our own, the following interface was setup. If we `extend`
 * the ParseError type instead, we would have incompatible types that
 * could not be expanded upon.
 */
export interface ParseInvoiceError {
	type: ParseInvoiceErrorType | ParseError['type'];
	code: ParseInvoiceErrorCode | ParseError['code'];

	/**
	 * @todo map this to expected translation keys
	 */
	message: ParseError['message'];
	messageHtml?: ParseError['message'];

	index?: ParseError['index'];

	row?: ParseError['row'];
}

export const ServerTimeoutError: ParseInvoiceError = {
	code: 'Aborted',
	type: 'ServerTimeout',
	message: 'Aborted: Please delete and try the upload again.',
};

export const UnauthorizedError: ParseInvoiceError = {
	code: 'InvalidOperation',
	type: 'Unauthorized',
	message:
		'Invalid Operation: This user does not have the proper permissions for this analysis.',
};

export const UnsupportedTypeError: ParseInvoiceError = {
	code: 'InvalidOperation',
	type: 'InsufficientData',
	message:
		'Invalid Operation: This file type is not supported. Please upload a CSV file.',
};

export const TooManyRowsError = (
	size: number,
	max: number,
): ParseInvoiceError => ({
	type: 'MaxAllotted',
	code: 'TooManyRows',
	message: `TooManyRows: The CSV includes ${size} rows which exceeds the allotted maximum of ${max}.`,
	messageHtml: `TooManyRows: The CSV includes <b>${size.toLocaleString()}</b> rows which exceeds the allotted maximum of <b>${max.toLocaleString()}</b>.`,
});

export const ParseInvoiceErrorCsvTemplate: (keyof ParseInvoiceError)[] = [
	'row',
	'index',
	'type',
	'code',
	'message',
] as const;
